import { all, call, put, takeLatest } from 'redux-saga/effects';
import { SagaIterator } from '@redux-saga/core';
import { statusMessageSaga } from '../AppManagement/saga';
import { RequestSagaParams } from '../../types';
import * as Actions from './slice';
import * as Endpoints from './endpoints';
import { getAssetFormData, mapSocialFeedPublishData } from './mapping';

export function* fetchChannelsByMarketer({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.fetchChannelsByMarketer, payload.marketerId);
    const channels = response.data?._embedded?.channels || [];
    yield put(Actions.fetchChannelsByMarketerSuccess(channels));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchChannelsByMarketerError(error));
  }
}

export function* fetchPagesByMarketer(): SagaIterator {
  try {
    const response = yield call(Endpoints.fetchStreamsByMarketer);
    const pages = response.data?._embedded?.streams || [];
    yield put(Actions.fetchPagesByMarketerSuccess(pages));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchPagesByMarketerError(error));
  }
}

export function* fetchFeedsByMarketer(): SagaIterator {
  try {
    const response = yield call(Endpoints.fetchPlacementsByMarketer);
    const feeds = response.data?._embedded?.placements || [];
    yield put(Actions.fetchFeedsByMarketerSuccess(feeds));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchFeedsByMarketerError(error));
  }
}

export function* fetchSocialFeedsByMarketer(): SagaIterator {
  try {
    const response = yield call(Endpoints.fetchSuperfeedsByMarketer);
    const socialFeeds = response.data?._embedded?.superfeeds || [];
    yield put(Actions.fetchSocialFeedsByMarketerSuccess(socialFeeds));
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchSocialFeedsByMarketerError(error));
  }
}

export function* fetchSocialFeedConfigurations({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.fetchSocialFeedConfiguration, payload.id);
    const socialFeeds = response.data || [];
    yield put(Actions.fetchSocialFeedConfigurationSuccess({ id: payload.id, data: socialFeeds }));
    if (payload.callback) {
      yield call(payload.callback, socialFeeds);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.fetchSocialFeedConfigurationError(error));
  }
}

export function* createPageForMarketer({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.createStreamForMarketer, payload.data);
    yield put(Actions.createPageForMarketerSuccess(response));
    yield call(statusMessageSaga, { type: 'success', message: 'Page created successfully' });

    yield call(fetchPagesByMarketer);

    if (payload.callback) {
      yield call(payload.callback);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.createPageForMarketerError(error));
  }
}

export function* createFeedForMarketer({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.createPlacementForMarketer, payload.data);
    yield put(Actions.createFeedForMarketerSuccess(response));
    yield call(fetchFeedsByMarketer);

    if (payload.callback) {
      const id = response?.data?.id;
      yield call(payload.callback, id);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Feed created successfully' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.createFeedForMarketerError(error));
  }
}

export function* updateFeedForMarketer({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.updatePlacementForMarketer, payload.data);
    yield put(Actions.updateFeedForMarketerSuccess(response));
    yield call(fetchFeedsByMarketer);
    if (payload.callback) {
      yield call(payload.callback);
    }
    yield call(statusMessageSaga, { type: 'success', message: 'Feed updated successfully' });
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.updateFeedForMarketerError(error));
  }
}

export function* createSocialFeedForMarketer({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.createSuperfeedForMarketer, payload.data);
    yield put(Actions.createSocialFeedForMarketerSuccess(response.data));
    yield call(statusMessageSaga, { type: 'success', message: 'Social Feed created successfully' });

    if (payload.callback) {
      yield call(payload.callback, response);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.createSocialFeedForMarketerError(error));
  }
}

export function* updateFeedProperties({ payload }: RequestSagaParams): SagaIterator {
  try {
    const response = yield call(Endpoints.updatePlacementProperties, payload.feedId, payload.properties);
    yield put(Actions.updateFeedPropertiesSuccess(response.data));
    yield call(statusMessageSaga, { type: 'success', message: 'Feed properties updated successfully' });

    if (payload.callback) {
      yield call(payload.callback);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.updateFeedPropertiesError(error));
  }
}

export function* updateGlobalSocialFeedSettings({ payload }: RequestSagaParams): SagaIterator {
  try {
    const { socialFeedId, settings } = payload.data;
    const response = yield call(Endpoints.updateGlobalSuperfeedSettings, socialFeedId, settings);
    yield put(Actions.updateGlobalSocialFeedSettingsSuccess(response.data));
    yield call(statusMessageSaga, { type: 'success', message: 'Social Feed global settings updated successfully' });

    yield call(fetchSocialFeedsByMarketer);

    if (payload.callback) {
      yield call(payload.callback);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.updateGlobalSocialFeedSettingsError(error));
  }
}

export function* updateGlobalMobileSocialFeedSettings({ payload }: RequestSagaParams): SagaIterator {
  try {
    const { socialFeedId, settings } = payload.data;
    const response = yield call(Endpoints.updateGlobalMobileSuperfeedSettings, socialFeedId, settings);
    yield put(Actions.updateGlobalMobileSocialFeedSettingsSuccess(response.data));
    yield call(statusMessageSaga, {
      type: 'success',
      message: 'Social Feed mobile global settings updated successfully'
    });

    if (payload.callback) {
      yield call(payload.callback);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.updateGlobalMobileSocialFeedSettingsError(error));
  }
}

export function* updateSocialFeedFilterSettings({ payload }: RequestSagaParams): SagaIterator {
  try {
    const { socialFeedId, settings } = payload.data;
    const response = yield call(Endpoints.updateSuperfeedFilterSettings, socialFeedId, settings);
    yield put(Actions.updateSocialFeedFilterSettingsSuccess(response.data));
    yield call(statusMessageSaga, {
      type: 'success',
      message: 'Social Feed filter settings updated successfully'
    });

    yield call(fetchSocialFeedsByMarketer);

    if (payload.callback) {
      yield call(payload.callback);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.updateSocialFeedFilterSettingsError(error));
  }
}

export function* updateSocialFeedMobileFilterSettings({ payload }: RequestSagaParams): SagaIterator {
  try {
    const { socialFeedId, settings } = payload.data;
    const response = yield call(Endpoints.updateSuperfeedMobileFilterSettings, socialFeedId, settings);
    yield put(Actions.updateSocialFeedMobileFilterSettingsSuccess(response.data));
    yield call(statusMessageSaga, {
      type: 'success',
      message: 'Social Feed mobile filter settings updated successfully'
    });

    if (payload.callback) {
      yield call(payload.callback);
    }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.updateSocialFeedMobileFilterSettingsError(error));
  }
}

export function* updateSocialFeedTitle({ payload }: RequestSagaParams): SagaIterator {
  try {
    yield call(Endpoints.updateSuperfeedTitle, payload.data);
    yield put(Actions.updateSocialFeedTitleSuccess());

    if (payload.callback) {
      yield call(payload.callback);
    }

    yield call(statusMessageSaga, {
      type: 'success',
      message: 'Successfully updated the social feed title'
    });
  } catch (error: any) {
    yield call(statusMessageSaga, {
      type: 'error',
      message: error?.message || error?.detail || 'Failed to update social feed title'
    });
    yield put(Actions.updateSocialFeedTitleError(error));
  }
}

export function* updateSocialFeedDescription({ payload }: RequestSagaParams): SagaIterator {
  try {
    yield call(Endpoints.updateSuperfeedDescription, payload.data);
    yield put(Actions.updateSocialFeedDescriptionSuccess());

    if (payload.callback) {
      yield call(payload.callback);
    }

    yield call(statusMessageSaga, {
      type: 'success',
      message: 'Successfully updated the social feed description'
    });
  } catch (error: any) {
    yield call(statusMessageSaga, {
      type: 'error',
      message: error?.message || error?.detail || 'Failed to update social feed description'
    });
    yield put(Actions.updateSocialFeedDescriptionError(error));
  }
}

export function* updateSocialFeedPrefix({ payload }: RequestSagaParams): SagaIterator {
  try {
    yield call(Endpoints.updateSuperfeedPrefix, payload.data);
    yield put(Actions.updateSocialFeedPrefixSuccess());

    if (payload.callback) {
      yield call(payload.callback);
    }

    yield call(statusMessageSaga, {
      type: 'success',
      message: 'Successfully updated the social feed prefix'
    });
  } catch (error: any) {
    yield call(statusMessageSaga, {
      type: 'error',
      message: error?.message || error?.detail || 'Failed to update social feed prefix'
    });
    yield put(Actions.updateSocialFeedPrefixError(error));
  }
}

export function* updateSocialFeedDomain({ payload }: RequestSagaParams): SagaIterator {
  try {
    yield call(Endpoints.updateSuperfeedDomain, payload.data);
    yield put(Actions.updateSocialFeedDomainSuccess());

    if (payload.callback) {
      yield call(payload.callback);
    }

    yield call(statusMessageSaga, {
      type: 'success',
      message: 'Successfully updated the social feed domain'
    });
  } catch (error: any) {
    yield call(statusMessageSaga, {
      type: 'error',
      message: error?.message || error?.detail || 'Failed to update social feed domain'
    });
    yield put(Actions.updateSocialFeedDomainError(error));
  }
}

export function* updateSocialFeedLogo({ payload }: RequestSagaParams): SagaIterator {
  try {
    yield call(Endpoints.updateSuperfeedLogo, payload.data);
    yield put(Actions.updateSocialFeedLogoSuccess());

    if (payload.callback) {
      yield call(payload.callback);
    }

    yield call(statusMessageSaga, {
      type: 'success',
      message: 'Successfully updated the social feed logo'
    });
  } catch (error: any) {
    yield call(statusMessageSaga, {
      type: 'error',
      message: error?.message || error?.detail || 'Failed to update social feed logo'
    });
    yield put(Actions.updateSocialFeedLogoError(error));
  }
}

export function* updateSocialFeedFavicon({ payload }: RequestSagaParams): SagaIterator {
  try {
    yield call(Endpoints.updateSuperfeedFavicon, payload.data);
    yield put(Actions.updateSocialFeedFaviconSuccess());

    if (payload.callback) {
      yield call(payload.callback);
    }

    yield call(statusMessageSaga, {
      type: 'success',
      message: 'Successfully updated the social feed favicon'
    });
  } catch (error: any) {
    yield call(statusMessageSaga, {
      type: 'error',
      message: error?.message || error?.detail || 'Failed to update social feed favicon'
    });
    yield put(Actions.updateSocialFeedFaviconError(error));
  }
}

export function* updatePage({ payload }: RequestSagaParams): SagaIterator {
  try {
    const { streamId, data } = payload;
    const response = yield call(Endpoints.updateStream, streamId, data);
    yield put(Actions.updatePageSuccess(response.data));

    yield call(fetchPagesByMarketer);

    if (payload.callback) {
      yield call(payload.callback);
    }

    yield call(statusMessageSaga, {
      type: 'success',
      message: 'Page updated successfully'
    });
  } catch (error: any) {
    yield call(statusMessageSaga, {
      type: 'error',
      message: error?.message || error?.detail || 'Failed to update page'
    });
    yield put(Actions.updatePageError(error));
  }
}

export function* updateCustomizeFeed({ payload }: RequestSagaParams): SagaIterator {
  try {
    const { feedId, properties } = payload;
    const webResponse = yield call(Endpoints.updatePlacementProperties, feedId, properties.properties);
    const mobileResponse = yield call(Endpoints.updatePlacementMobileProperties, feedId, properties.mobileProperties);
    yield put(Actions.updateCustomizeFeedSuccess(mobileResponse.data));
    if (payload.callback) {
      yield call(payload.callback);
    }
    yield call(statusMessageSaga, {
      type: 'success',
      message: 'Feed Customised successfully'
    });
  } catch (error: any) {
    yield call(statusMessageSaga, {
      type: 'error',
      message: error?.message || error?.detail || 'Failed to update page'
    });
    yield put(Actions.updateCustomizeFeedError(error));
  }
}

export function* publishSocialFeed({ payload }: RequestSagaParams): SagaIterator {
  try {
    const { socialFeed, settings, currentConfig } = payload.data;

    const mapperObject = yield call(mapSocialFeedPublishData, settings, currentConfig, socialFeed);

    const errorMessages = [];
    let isProcessed = false;
    if (mapperObject.webGlobalSettings) {
      isProcessed = true;
      try {
        if (mapperObject.webGlobalSettings.logo instanceof File) {
          const logoUploadResponse = yield call(
            Endpoints.putMediaContent,
            getAssetFormData(mapperObject.webGlobalSettings.logo),
            socialFeed.id,
            'superfeedlogo'
          );
          mapperObject.webGlobalSettings.logo = logoUploadResponse?.data?._embedded?.assets[0]._links?.access?.href;
        }
        const response = yield call(
          Endpoints.updateGlobalSuperfeedSettings,
          socialFeed.id,
          mapperObject.webGlobalSettings
        );
        yield put(Actions.publishSocialFeedUpdate(response.data));
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }
    if (mapperObject.mobileGlobalSettings) {
      isProcessed = true;
      try {
        if (mapperObject.mobileGlobalSettings.logo instanceof File) {
          const logoUploadResponse = yield call(
            Endpoints.putMediaContent,
            getAssetFormData(mapperObject.mobileGlobalSettings.logo),
            socialFeed.id,
            'superfeedmobilelogo'
          );
          mapperObject.mobileGlobalSettings.logo = logoUploadResponse?.data?._embedded?.assets[0]._links?.access?.href;
        }
        const response = yield call(
          Endpoints.updateGlobalMobileSuperfeedSettings,
          socialFeed.id,
          mapperObject.mobileGlobalSettings
        );
        yield put(Actions.publishSocialFeedUpdate(response.data));
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }
    if (mapperObject.webFilters) {
      isProcessed = true;
      try {
        for (const filter of mapperObject.webFilters.filterValues) {
          if (filter.file instanceof File) {
            const iconUploadResponse = yield call(
              Endpoints.putMediaContent,
              getAssetFormData(filter.file),
              socialFeed.id,
              'superfeedfiltericon' + crypto.randomUUID()
            );
            filter.icon = iconUploadResponse?.data?._embedded?.assets[0]._links?.access?.href;
            delete filter.file;
          }
        }

        const response = yield call(Endpoints.updateSuperfeedFilterSettings, socialFeed.id, mapperObject.webFilters);
        yield put(Actions.publishSocialFeedUpdate(response.data));
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }
    if (mapperObject.mobileFilter) {
      isProcessed = true;
      try {
        for (const filter of mapperObject.mobileFilter.filterValues) {
          if (filter.file instanceof File) {
            const iconUploadResponse = yield call(
              Endpoints.putMediaContent,
              getAssetFormData(filter.file),
              socialFeed.id,
              'superfeedfiltericon' + crypto.randomUUID()
            );
            filter.icon = iconUploadResponse?.data?._embedded?.assets[0]._links?.access?.href;
            delete filter.file;
          }
        }
        const response = yield call(
          Endpoints.updateSuperfeedMobileFilterSettings,
          socialFeed.id,
          mapperObject.mobileFilter
        );
        yield put(Actions.publishSocialFeedUpdate(response.data));
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.webBannerSettings) {
      isProcessed = true;
      try {
        const response = yield call(
          Endpoints.updateWebBannerConfiguration,
          socialFeed.id,
          mapperObject.webBannerSettings
        );
        yield put(
          Actions.updateSocialFeedBannerConfiguration({
            id: socialFeed.id,
            value: response.data,
            device: 'web'
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.wedFeedPlayerSettings) {
      isProcessed = true;
      try {
        const response = yield call(
          Endpoints.updateWebFeedPlayerConfiguration,
          socialFeed.id,
          mapperObject.wedFeedPlayerSettings
        );
        yield put(
          Actions.updateSocialFeedPlayerConfiguration({
            id: socialFeed.id,
            key: 'player',
            device: 'web',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.webFeedShareSettings) {
      isProcessed = true;
      try {
        const response = yield call(
          Endpoints.updateWebFeedShareConfiguration,
          socialFeed.id,
          mapperObject.webFeedShareSettings
        );
        yield put(
          Actions.updateSocialFeedPlayerConfiguration({
            id: socialFeed.id,
            key: 'share',
            device: 'web',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.webFeedCommentSettings) {
      isProcessed = true;
      try {
        const response = yield call(
          Endpoints.updateWebFeedCommentsConfiguration,
          socialFeed.id,
          mapperObject.webFeedCommentSettings
        );
        yield put(
          Actions.updateSocialFeedPlayerConfiguration({
            id: socialFeed.id,
            key: 'comment',
            device: 'web',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.webNavigationHomeSettings) {
      isProcessed = true;
      try {
        if (
          mapperObject.webNavigationHomeSettings.icon.file &&
          mapperObject.webNavigationHomeSettings.icon.file instanceof File
        ) {
          const logoUploadResponse = yield call(
            Endpoints.putMediaContent,
            getAssetFormData(mapperObject.webNavigationHomeSettings.icon.file),
            socialFeed.id,
            'superfeedHomeIcon'
          );
          mapperObject.webNavigationHomeSettings.icon =
            logoUploadResponse?.data?._embedded?.assets[0]._links?.access?.href;
          mapperObject.webNavigationHomeSettings.iconType = 'custom';
        } else if (mapperObject.webNavigationHomeSettings.icon.type === 'EMOJI') {
          mapperObject.webNavigationHomeSettings.icon = mapperObject.webNavigationHomeSettings.icon.name;
          mapperObject.webNavigationHomeSettings.iconType = 'emoji';
        }

        const response = yield call(
          Endpoints.updateWebNavigationHomeConfiguration,
          socialFeed.id,
          mapperObject.webNavigationHomeSettings
        );
        yield put(
          Actions.updateSocialFeedNavigationConfiguration({
            id: socialFeed.id,
            key: 'home',
            device: 'web',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.webNavigationFeedSettings) {
      isProcessed = true;
      try {
        if (
          mapperObject.webNavigationFeedSettings.icon.file &&
          mapperObject.webNavigationFeedSettings.icon.file instanceof File
        ) {
          const logoUploadResponse = yield call(
            Endpoints.putMediaContent,
            getAssetFormData(mapperObject.webNavigationFeedSettings.icon.file),
            socialFeed.id,
            'superfeedPlayerIcon'
          );
          mapperObject.webNavigationFeedSettings.icon =
            logoUploadResponse?.data?._embedded?.assets[0]._links?.access?.href;
          mapperObject.webNavigationFeedSettings.iconType = 'custom';
        } else {
          mapperObject.webNavigationFeedSettings.icon = mapperObject.webNavigationFeedSettings.icon.name;
          mapperObject.webNavigationFeedSettings.iconType = 'emoji';
        }

        const response = yield call(
          Endpoints.updateWebNavigationFeedConfiguration,
          socialFeed.id,
          mapperObject.webNavigationFeedSettings
        );
        yield put(
          Actions.updateSocialFeedNavigationConfiguration({
            id: socialFeed.id,
            key: 'feed',
            device: 'web',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.webNavigationUserProfileSettings) {
      isProcessed = true;
      try {
        if (
          mapperObject.webNavigationUserProfileSettings.icon.file &&
          mapperObject.webNavigationUserProfileSettings.icon.file instanceof File
        ) {
          const logoUploadResponse = yield call(
            Endpoints.putMediaContent,
            getAssetFormData(mapperObject.webNavigationUserProfileSettings.icon.file),
            socialFeed.id,
            'superfeedUserProfileIcon'
          );
          mapperObject.webNavigationUserProfileSettings.icon =
            logoUploadResponse?.data?._embedded?.assets[0]._links?.access?.href;
          mapperObject.webNavigationUserProfileSettings.iconType = 'custom';
        } else {
          mapperObject.webNavigationUserProfileSettings.icon = mapperObject.webNavigationUserProfileSettings.icon.name;
          mapperObject.webNavigationUserProfileSettings.iconType = 'emoji';
        }

        const response = yield call(
          Endpoints.updateWebNavigationUserProfileConfiguration,
          socialFeed.id,
          mapperObject.webNavigationUserProfileSettings
        );
        yield put(
          Actions.updateSocialFeedNavigationConfiguration({
            id: socialFeed.id,
            key: 'userProfile',
            device: 'web',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.webNavigationCourseSettings) {
      isProcessed = true;
      try {
        const response = yield call(
          Endpoints.updateWebNavigationCourseConfiguration,
          socialFeed.id,
          mapperObject.webNavigationCourseSettings
        );
        yield put(
          Actions.updateSocialFeedNavigationConfiguration({
            id: socialFeed.id,
            key: 'course',
            device: 'web',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.mobileBannerSettings) {
      isProcessed = true;
      try {
        const response = yield call(
          Endpoints.updateMobileBannerConfiguration,
          socialFeed.id,
          mapperObject.mobileBannerSettings
        );
        yield put(
          Actions.updateSocialFeedBannerConfiguration({
            id: socialFeed.id,
            value: response.data,
            device: 'mobile'
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.mobileFeedPlayerSettings) {
      isProcessed = true;
      try {
        const response = yield call(
          Endpoints.updateMobileFeedPlayerConfiguration,
          socialFeed.id,
          mapperObject.mobileFeedPlayerSettings
        );
        yield put(
          Actions.updateSocialFeedPlayerConfiguration({
            id: socialFeed.id,
            key: 'player',
            device: 'mobile',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.mobileFeedShareSettings) {
      isProcessed = true;
      try {
        const response = yield call(
          Endpoints.updateMobileFeedShareConfiguration,
          socialFeed.id,
          mapperObject.mobileFeedShareSettings
        );
        yield put(
          Actions.updateSocialFeedPlayerConfiguration({
            id: socialFeed.id,
            key: 'share',
            device: 'mobile',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.mobileFeedCommentSettings) {
      isProcessed = true;
      try {
        const response = yield call(
          Endpoints.updateMobileFeedCommentsConfiguration,
          socialFeed.id,
          mapperObject.mobileFeedCommentSettings
        );
        yield put(
          Actions.updateSocialFeedPlayerConfiguration({
            id: socialFeed.id,
            key: 'comment',
            device: 'mobile',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.mobileNavigationHomeSettings) {
      isProcessed = true;
      try {
        if (
          mapperObject.mobileNavigationHomeSettings.icon.file &&
          mapperObject.mobileNavigationHomeSettings.icon.file instanceof File
        ) {
          const logoUploadResponse = yield call(
            Endpoints.putMediaContent,
            getAssetFormData(mapperObject.mobileNavigationHomeSettings.icon.file),
            socialFeed.id,
            'superfeedHomeMobileIcon'
          );
          mapperObject.mobileNavigationHomeSettings.icon =
            logoUploadResponse?.data?._embedded?.assets[0]._links?.access?.href;
          mapperObject.mobileNavigationHomeSettings.iconType = 'custom';
        } else if (mapperObject.mobileNavigationHomeSettings.icon.type === 'EMOJI') {
          mapperObject.mobileNavigationHomeSettings.icon = mapperObject.mobileNavigationHomeSettings.icon.name;
          mapperObject.mobileNavigationHomeSettings.iconType = 'emoji';
        }

        const response = yield call(
          Endpoints.updateMobileNavigationHomeConfiguration,
          socialFeed.id,
          mapperObject.mobileNavigationHomeSettings
        );
        yield put(
          Actions.updateSocialFeedNavigationConfiguration({
            id: socialFeed.id,
            key: 'home',
            device: 'mobile',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.mobileNavigationFeedSettings) {
      isProcessed = true;
      try {
        if (
          mapperObject.mobileNavigationFeedSettings.icon.file &&
          mapperObject.mobileNavigationFeedSettings.icon.file instanceof File
        ) {
          const logoUploadResponse = yield call(
            Endpoints.putMediaContent,
            getAssetFormData(mapperObject.mobileNavigationFeedSettings.icon.file),
            socialFeed.id,
            'superfeedPlayerMobileIcon'
          );
          mapperObject.mobileNavigationFeedSettings.icon =
            logoUploadResponse?.data?._embedded?.assets[0]._links?.access?.href;
          mapperObject.mobileNavigationFeedSettings.iconType = 'custom';
        } else {
          mapperObject.mobileNavigationFeedSettings.icon = mapperObject.mobileNavigationFeedSettings.icon.name;
          mapperObject.mobileNavigationFeedSettings.iconType = 'emoji';
        }

        const response = yield call(
          Endpoints.updateMobileNavigationFeedConfiguration,
          socialFeed.id,
          mapperObject.mobileNavigationFeedSettings
        );
        yield put(
          Actions.updateSocialFeedNavigationConfiguration({
            id: socialFeed.id,
            key: 'feed',
            device: 'mobile',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.mobileNavigationUserProfileSettings) {
      isProcessed = true;
      try {
        if (
          mapperObject.mobileNavigationUserProfileSettings.icon.file &&
          mapperObject.mobileNavigationUserProfileSettings.icon.file instanceof File
        ) {
          const logoUploadResponse = yield call(
            Endpoints.putMediaContent,
            getAssetFormData(mapperObject.mobileNavigationUserProfileSettings.icon.file),
            socialFeed.id,
            'superfeedUserProfileMobileIcon'
          );
          mapperObject.mobileNavigationUserProfileSettings.icon =
            logoUploadResponse?.data?._embedded?.assets[0]._links?.access?.href;
          mapperObject.mobileNavigationUserProfileSettings.iconType = 'custom';
        } else {
          mapperObject.mobileNavigationUserProfileSettings.icon =
            mapperObject.mobileNavigationUserProfileSettings.icon.name;
          mapperObject.mobileNavigationUserProfileSettings.iconType = 'emoji';
        }

        const response = yield call(
          Endpoints.updateMobileNavigationUserProfileConfiguration,
          socialFeed.id,
          mapperObject.mobileNavigationUserProfileSettings
        );
        yield put(
          Actions.updateSocialFeedNavigationConfiguration({
            id: socialFeed.id,
            key: 'userProfile',
            device: 'mobile',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (mapperObject.mobileNavigationCourseSettings) {
      isProcessed = true;
      try {
        const response = yield call(
          Endpoints.updateMobileNavigationCourseConfiguration,
          socialFeed.id,
          mapperObject.mobileNavigationCourseSettings
        );
        yield put(
          Actions.updateSocialFeedNavigationConfiguration({
            id: socialFeed.id,
            key: 'course',
            device: 'mobile',
            value: response.data
          })
        );
      } catch (error: any) {
        errorMessages.push(error?.message || error?.detail);
      }
    }

    if (isProcessed) {
      // yield call(fetchSocialFeedsByMarketer);

      if (errorMessages.length > 0) {
        yield call(statusMessageSaga, { type: 'error', message: errorMessages.join(', ') });
        yield put(Actions.publishSocialFeedError(errorMessages));
      } else {
        if (payload.callback) {
          yield call(payload.callback);
        }
        yield put(Actions.publishSocialFeedSuccess({}));
        yield call(statusMessageSaga, { type: 'success', message: 'Social Feed settings updated successfully' });
      }
    } else {
      if (payload.callback) {
        yield call(payload.callback);
      }
      yield put(Actions.publishSocialFeedSuccess({}));
      yield call(statusMessageSaga, { type: 'error', message: 'No changes were made to the settings' });
    }

    // const yieldArray = [];
    // if (mapperObject.webGlobalSettings) {
    //   yieldArray.push(call(Endpoints.updateGlobalSuperfeedSettings, socialFeed.id, mapperObject.webGlobalSettings));
    // }
    // if (mapperObject.mobileGlobalSettings) {
    //   yieldArray.push(call(Endpoints.updateGlobalMobileSuperfeedSettings, socialFeed.id, mapperObject.mobileGlobalSettings));
    // }
    // if (mapperObject.webFilters) {
    //   yieldArray.push(call(Endpoints.updateSuperfeedFilterSettings, socialFeed.id, mapperObject.webFilters));
    // }
    // if (mapperObject.mobileFilter) {
    //   yieldArray.push(call(Endpoints.updateSuperfeedMobileFilterSettings, socialFeed.id, mapperObject.mobileFilter));
    // }

    // if (yieldArray.length > 0) {
    //   try {
    //     yield all(yieldArray);
    //     yield call(fetchSocialFeedsByMarketer);
    //     yield put(Actions.publishSocialFeedSuccess({}));
    //     yield call(statusMessageSaga, { type: 'success', message: 'Social Feed settings updated successfully' });

    //   } catch (error: any) {
    //     yield call(fetchSocialFeedsByMarketer);
    //     yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    //     yield put(Actions.publishSocialFeedError(error));
    //   }
    // }else{
    //   yield put(Actions.publishSocialFeedSuccess({}));
    //   yield call(statusMessageSaga, { type: 'success', message: 'No changes were made to the settings' });

    // }
  } catch (error: any) {
    yield call(statusMessageSaga, { type: 'error', message: error?.message || error?.detail });
    yield put(Actions.publishSocialFeedError(error));
  }
}

export function* updateSocialFeedContent({ payload }: RequestSagaParams): SagaIterator {
  try {
    const { feed, formData } = payload;
    let response;
    if (feed.title !== formData.title) {
      response = yield call(Endpoints.updateSuperfeedTitle, { id: feed.id, title: formData.title });
    }

    if (feed.description !== formData.description) {
      response = yield call(Endpoints.updateSuperfeedDescription, { id: feed.id, description: formData.description });
    }

    if (formData.favicon instanceof File) {
      const iconUploadResponse = yield call(
        Endpoints.putMediaContent,
        getAssetFormData(formData.favicon),
        feed.id,
        'superfeedFavicon'
      );
      response = yield call(Endpoints.updateSuperfeedFavicon, {
        id: feed.id,
        favicon: iconUploadResponse?.data?._embedded?.assets[0]._links?.access?.href
      });
    }

    if (formData.preview instanceof File) {
      const iconUploadResponse = yield call(
        Endpoints.putMediaContent,
        getAssetFormData(formData.preview),
        feed.id,
        'superfeedPreview'
      );
      response = yield call(Endpoints.updateSuperfeedPreviewImage, {
        id: feed.id,
        previewImage: iconUploadResponse?.data?._embedded?.assets[0]._links?.access?.href
      });
    }

    // const webResponse = yield call(Endpoints.updatePlacementProperties, feedId, properties.properties);
    // const mobileResponse = yield call(Endpoints.updatePlacementMobileProperties, feedId, properties.mobileProperties);
    yield put(Actions.updateSocialFeedContentSuccess(response.data));
    // if (payload.callback) {
    //   yield call(payload.callback);
    // }
    yield call(statusMessageSaga, {
      type: 'success',
      message: 'Super Feed content updated successfully'
    });
  } catch (error: any) {
    yield call(statusMessageSaga, {
      type: 'error',
      message: error?.message || error?.detail || 'Failed to update super feed content'
    });
    yield put(Actions.updateSocialFeedContentError(error));
  }
}

export function* FeedManagementWatcherSaga(): SagaIterator {
  yield all([
    yield takeLatest(Actions.fetchChannelsByMarketer.type, fetchChannelsByMarketer),
    yield takeLatest(Actions.fetchPagesByMarketer.type, fetchPagesByMarketer),
    yield takeLatest(Actions.fetchFeedsByMarketer.type, fetchFeedsByMarketer),
    yield takeLatest(Actions.fetchSocialFeedsByMarketer.type, fetchSocialFeedsByMarketer),
    yield takeLatest(Actions.createPageForMarketer.type, createPageForMarketer),
    yield takeLatest(Actions.createFeedForMarketer.type, createFeedForMarketer),
    yield takeLatest(Actions.updateFeedForMarketer.type, updateFeedForMarketer),
    yield takeLatest(Actions.updatePage.type, updatePage),
    yield takeLatest(Actions.createSocialFeedForMarketer.type, createSocialFeedForMarketer),
    yield takeLatest(Actions.updateGlobalSocialFeedSettings.type, updateGlobalSocialFeedSettings),
    yield takeLatest(Actions.updateGlobalMobileSocialFeedSettings.type, updateGlobalMobileSocialFeedSettings),
    yield takeLatest(Actions.updateSocialFeedFilterSettings.type, updateSocialFeedFilterSettings),
    yield takeLatest(Actions.updateSocialFeedMobileFilterSettings.type, updateSocialFeedMobileFilterSettings),
    yield takeLatest(Actions.updateFeedProperties.type, updateFeedProperties),
    yield takeLatest(Actions.updateSocialFeedTitle.type, updateSocialFeedTitle),
    yield takeLatest(Actions.updateSocialFeedDescription.type, updateSocialFeedDescription),
    yield takeLatest(Actions.updateSocialFeedPrefix.type, updateSocialFeedPrefix),
    yield takeLatest(Actions.updateSocialFeedDomain.type, updateSocialFeedDomain),
    yield takeLatest(Actions.updateSocialFeedLogo.type, updateSocialFeedLogo),
    yield takeLatest(Actions.updateSocialFeedFavicon.type, updateSocialFeedFavicon),
    yield takeLatest(Actions.updateCustomizeFeed.type, updateCustomizeFeed),
    yield takeLatest(Actions.publishSocialFeed.type, publishSocialFeed),
    yield takeLatest(Actions.updateSocialFeedContent.type, updateSocialFeedContent),
    yield takeLatest(Actions.fetchSocialFeedConfiguration.type, fetchSocialFeedConfigurations)
  ]);
}

export default FeedManagementWatcherSaga;
